import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/News.css';

const News = () => {
  const [newsData, setNewsData] = useState(null);
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [apiStatus, setApiStatus] = useState('loading'); // 'loading', 'success', 'error'

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch news data from backend
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        setLoading(true);
        setError(null);
        setApiStatus('loading');
        
        console.log('🔄 Fetching news data from API...');
        console.log('📡 API URL:', API_BASE_URL);
        
        // Fetch different types of news
        const [allNewsResponse, featuredNewsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/news?limit=5`),
          fetch(`${API_BASE_URL}/api/news/featured?limit=1`)
        ]);

        if (!allNewsResponse.ok || !featuredNewsResponse.ok) {
          throw new Error(`Server error: ${allNewsResponse.status} ${allNewsResponse.statusText}`);
        }

        const allNewsData = await allNewsResponse.json();
        const featuredNewsData = await featuredNewsResponse.json();

        console.log('✅ News data fetched successfully');
        console.log('📄 All news:', allNewsData);
        console.log('⭐ Featured news:', featuredNewsData);

        // Process and categorize news data
        const processedData = processNewsData(allNewsData.data || [], featuredNewsData.data || []);
        setNewsData(processedData);
        setApiStatus('success');
        
      } catch (err) {
        console.error('❌ Error fetching news:', err);
        setError(err.message || 'Không thể tải tin tức. Vui lòng thử lại sau.');
        setApiStatus('error');
        
        // Use fallback data if API fails
        setNewsData(getFallbackData());
      } finally {
        setLoading(false);
      }
    };

    fetchNewsData();
  }, [API_BASE_URL]);

  // Fetch jobs data from backend
  useEffect(() => {
    const fetchJobsData = async () => {
      try {
        setJobsLoading(true);
        setJobsError(null);
        
        console.log('💼 Fetching jobs data from API...');
        
        const jobsResponse = await fetch(`${API_BASE_URL}/api/jobs`);
        
        if (!jobsResponse.ok) {
          throw new Error(`Jobs API error: ${jobsResponse.status}`);
        }

        const jobsResult = await jobsResponse.json();
        console.log('✅ Jobs data fetched successfully:', jobsResult);

        if (jobsResult.success && jobsResult.data) {
          setJobsData(jobsResult.data);
        } else {
          setJobsData([]);
        }
        
      } catch (err) {
        console.error('❌ Error fetching jobs:', err);
        setJobsError(err.message || 'Không thể tải dữ liệu tuyển dụng');
        
        // Use fallback jobs data
        setJobsData(getFallbackJobsData());
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobsData();
  }, [API_BASE_URL]);

  // Process news data into categories
  const processNewsData = (allNews, featuredNews) => {
    const categories = {
      iot: [],
      architecture: [],
      solar: [],
      landscape: []
    };

    const latest = [];

    // Ensure allNews is an array
    const newsArray = Array.isArray(allNews) ? allNews : [];
    const featuredArray = Array.isArray(featuredNews) ? featuredNews : [];

    // Categorize news
    newsArray.forEach(news => {
      const newsItem = {
        id: news.id,
        title: news.title,
        featured_image: news.image || '/images/placeholder-news.svg',
        date: formatDate(news.createdAt || news.date),
        category_name: news.category || 'Tin tức',
        slug: news.slug || createSlug(news.title),
        excerpt: news.excerpt || news.description || '',
        views: news.views || 0
      };

      // Add to appropriate category
      const category = news.category?.toLowerCase();
      if (category && categories[category]) {
        categories[category].push(newsItem);
      }

      // Add to latest (limit to 5)
      if (latest.length < 5) {
        latest.push({
          ...newsItem,
          views: news.views || Math.floor(Math.random() * 3000) + 500
        });
      }
    });

    return {
      featured: featuredArray.map(news => ({
        id: news.id,
        title: news.title,
        excerpt: news.excerpt || news.description || news.content?.substring(0, 200) + '...',
        featured_image: news.image || '/images/placeholder-news.svg',
        date: formatDate(news.createdAt || news.date),
        category_name: news.category || 'Tin nổi bật',
        slug: news.slug || createSlug(news.title),
        is_featured: true
      })),
      categories,
      latest
    };
  };

  // Format salary range
  const formatSalary = (minSalary, maxSalary) => {
    if (!minSalary && !maxSalary) return 'Thỏa thuận';
    
    const formatAmount = (amount) => {
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(0)} triệu`;
      }
      return `${amount.toLocaleString('vi-VN')}`;
    };

    if (minSalary && maxSalary) {
      return `${formatAmount(minSalary)} - ${formatAmount(maxSalary)}`;
    } else if (minSalary) {
      return `Từ ${formatAmount(minSalary)}`;
    } else {
      return `Lên đến ${formatAmount(maxSalary)}`;
    }
  };

  // Format deadline
  const formatDeadline = (deadline) => {
    if (!deadline) return 'Không giới hạn';
    
    try {
      const date = new Date(deadline);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return deadline;
    }
  };

  // Get department display name
  const getDepartmentName = (department) => {
    const departments = {
      'engineering': 'Kỹ thuật',
      'marketing': 'Marketing',
      'sales': 'Kinh doanh',
      'hr': 'Nhân sự',
      'finance': 'Tài chính',
      'operations': 'Vận hành',
      'design': 'Thiết kế',
      'product': 'Sản phẩm'
    };
    return departments[department] || department;
  };

  // Get job type display name
  const getJobTypeName = (jobType) => {
    const types = {
      'full-time': 'Toàn thời gian',
      'part-time': 'Bán thời gian',
      'contract': 'Hợp đồng',
      'internship': 'Thực tập',
      'freelance': 'Freelance'
    };
    return types[jobType] || jobType;
  };

  // Fallback jobs data
  const getFallbackJobsData = () => [
    {
      id: 1,
      title: 'Kỹ sư IoT - Mức lương hấp dẫn',
      slug: 'ky-su-iot-luong-hap-dan',
      department: 'engineering',
      job_type: 'full-time',
      min_salary: 15000000,
      max_salary: 25000000,
      location: 'TP.HCM',
      application_deadline: '2024-12-31',
      contact_email: 'hr@phucan.vn',
      image: ''
    },
    {
      id: 2,
      title: 'Kiến trúc sư - Thu nhập cao',
      slug: 'kien-truc-su-thu-nhap-cao',
      department: 'design',
      job_type: 'full-time',
      min_salary: 20000000,
      max_salary: 35000000,
      location: 'TP.HCM',
      application_deadline: '2025-01-05',
      contact_email: 'hr@phucan.vn',
      image: ''
    },
    {
      id: 3,
      title: 'Chuyên viên Marketing Digital',
      slug: 'chuyen-vien-marketing-digital',
      department: 'marketing',
      job_type: 'full-time',
      min_salary: 12000000,
      max_salary: 18000000,
      location: 'TP.HCM',
      application_deadline: '2024-12-28',
      contact_email: 'hr@phucan.vn',
      image: ''
    },
    {
      id: 4,
      title: 'Sales Engineer - Solar Energy',
      slug: 'sales-engineer-solar-energy',
      department: 'sales',
      job_type: 'full-time',
      min_salary: 10000000,
      max_salary: 15000000,
      location: 'TP.HCM',
      application_deadline: '2025-01-10',
      contact_email: 'hr@phucan.vn',
      image: ''
    },
    {
      id: 5,
      title: 'Thực tập sinh Fullstack Developer',
      slug: 'thuc-tap-sinh-fullstack-developer',
      department: 'engineering',
      job_type: 'internship',
      min_salary: 3000000,
      max_salary: 5000000,
      location: 'TP.HCM',
      application_deadline: '2024-12-20',
      contact_email: 'hr@phucan.vn',
      image: ''
    },
    {
      id: 6,
      title: 'Project Manager - Smart Building',
      slug: 'project-manager-smart-building',
      department: 'operations',
      job_type: 'full-time',
      min_salary: 20000000,
      max_salary: 30000000,
      location: 'TP.HCM',
      application_deadline: '2025-01-15',
      contact_email: 'hr@phucan.vn',
      image: ''
    }
  ];

  // Create slug from title
  const createSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get image URL
  const getImageUrl = (item) => {
    const image = item.featured_image || item.image;
    if (!image || image === '/images/placeholder-news.svg') {
      return '/images/placeholder-news.svg';
    }
    
    // If image starts with /uploads, add API base URL
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  // Get job image URL (matching News image pattern)
  const getJobImageUrl = (job) => {
    const image = job.image;
    if (!image) {
      return '/images';
    }
    
    // If image starts with /uploads, add API base URL
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  // Get views count
  const getViewsCount = (item) => {
    return item.views_count || item.views || 0;
  };

  // Fallback data function
  const getFallbackData = () => ({
    featured: [{
      id: 1,
      title: 'Công ty PhucAn Solutions đạt giải thưởng "Doanh nghiệp tiêu biểu 2024"',
      excerpt: 'Với những thành tích xuất sắc trong lĩnh vực công nghệ và năng lượng sạch, PhucAn Solutions vinh dự nhận giải thưởng danh giá...',
      featured_image: '/images/news-featured-1.jpg',
      date: '15/12/2024',
      category_name: 'Tin nổi bật',
      slug: 'giai-thuong-doanh-nghiep-tieu-bieu-2024',
      is_featured: true
    }],
    categories: {
      iot: [
        {
          id: 1,
          title: 'Xu hướng IoT trong smart home 2024',
          featured_image: '/images/iot-news-1.jpg',
          date: '10/12/2024',
          category_name: 'IoT',
          slug: 'xu-huong-iot-smart-home-2024'
        },
        {
          id: 2,
          title: 'Ứng dụng AI trong hệ thống CCTV thông minh',
          featured_image: '/images/iot-news-2.jpg',
          date: '08/12/2024',
          category_name: 'IoT',
          slug: 'ai-cctv-thong-minh'
        },
        {
          id: 3,
          title: 'Smart Home - Tương lai của ngôi nhà hiện đại',
          featured_image: '/images/iot-news-3.jpg',
          date: '05/12/2024',
          category_name: 'IoT',
          slug: 'smart-home-tuong-lai'
        }
      ],
      architecture: [
        {
          id: 4,
          title: 'Xu hướng kiến trúc xanh 2024',
          featured_image: '/images/arch-news-1.jpg',
          date: '12/12/2024',
          category_name: 'Kiến trúc',
          slug: 'xu-huong-kien-truc-xanh-2024'
        },
        {
          id: 5,
          title: 'Thiết kế nhà ở bền vững và thân thiện môi trường',
          featured_image: '/images/arch-news-2.jpg',
          date: '09/12/2024',
          category_name: 'Kiến trúc',
          slug: 'thiet-ke-nha-ben-vung'
        },
        {
          id: 6,
          title: 'Công nghệ BIM trong thiết kế và thi công',
          featured_image: '/images/arch-news-3.jpg',
          date: '06/12/2024',
          category_name: 'Kiến trúc',
          slug: 'cong-nghe-bim'
        }
      ],
      energy: [
        {
          id: 7,
          title: 'Lợi ích của năng lượng mặt trời cho gia đình',
          featured_image: '/images/solar-news-1.jpg',
          date: '11/12/2024',
          category_name: 'Năng lượng',
          slug: 'loi-ich-nang-luong-mat-troi'
        },
        {
          id: 8,
          title: 'Hướng dẫn lắp đặt hệ thống điện mặt trời',
          featured_image: '/images/solar-news-2.jpg',
          date: '07/12/2024',
          category_name: 'Năng lượng',
          slug: 'huong-dan-lap-dat-dien-mat-troi'
        },
        {
          id: 9,
          title: 'Chính sách khuyến khích sử dụng năng lượng sạch',
          featured_image: '/images/solar-news-3.jpg',
          date: '04/12/2024',
          category_name: 'Năng lượng',
          slug: 'chinh-sach-nang-luong-sach'
        }
      ],
      garden: [
        {
          id: 10,
          title: 'Thiết kế sân vườn nhỏ cho căn hộ chung cư',
          featured_image: '/images/garden-news-1.jpg',
          date: '13/12/2024',
          category_name: 'Sân vườn',
          slug: 'thiet-ke-san-vuon-nho'
        },
        {
          id: 11,
          title: 'Cây xanh phù hợp cho khí hậu nhiệt đới',
          featured_image: '/images/garden-news-2.jpg',
          date: '10/12/2024',
          category_name: 'Sân vườn',
          slug: 'cay-xanh-khi-hau-nhiet-doi'
        },
        {
          id: 12,
          title: 'Hệ thống tưới tự động cho vườn nhà',
          featured_image: '/images/garden-news-3.jpg',
          date: '07/12/2024',
          category_name: 'Sân vườn',
          slug: 'he-thong-tuoi-tu-dong'
        }
      ]
    },
    latest: [
      {
        id: 1,
        title: 'Công ty PhucAn Solutions đạt giải thưởng "Doanh nghiệp tiêu biểu 2024"',
        date: '15/12/2024',
        views: 2500,
        category_name: 'Tin nổi bật',
        slug: 'giai-thuong-doanh-nghiep-tieu-bieu-2024'
      },
      {
        id: 2,
        title: 'Xu hướng kiến trúc xanh 2024',
        date: '12/12/2024',
        views: 1800,
        category_name: 'Kiến trúc',
        slug: 'xu-huong-kien-truc-xanh-2024'
      },
      {
        id: 3,
        title: 'Lợi ích của năng lượng mặt trời cho gia đình',
        date: '11/12/2024',
        views: 2100,
        category_name: 'Năng lượng',
        slug: 'loi-ich-nang-luong-mat-troi'
      },
      {
        id: 4,
        title: 'Xu hướng IoT trong smart home 2024',
        date: '10/12/2024',
        views: 1600,
        category_name: 'IoT',
        slug: 'xu-huong-iot-smart-home-2024'
      },
      {
        id: 5,
        title: 'Thiết kế sân vườn nhỏ cho căn hộ chung cư',
        date: '13/12/2024',
        views: 1200,
        category_name: 'Sân vườn',
        slug: 'thiet-ke-san-vuon-nho'
      }
    ]
  });

  const displayData = newsData || getFallbackData();

  // Safe access to ensure arrays are always defined
  const safeDisplayData = {
    featured: Array.isArray(displayData.featured) ? displayData.featured : [],
    categories: {
      iot: Array.isArray(displayData.categories?.iot) ? displayData.categories.iot : [],
      architecture: Array.isArray(displayData.categories?.architecture) ? displayData.categories.architecture : [],
      solar: Array.isArray(displayData.categories?.solar) ? displayData.categories.solar : [],
      landscape: Array.isArray(displayData.categories?.landscape) ? displayData.categories.landscape : [],
      energy: Array.isArray(displayData.categories?.energy) ? displayData.categories.energy : [],
      garden: Array.isArray(displayData.categories?.garden) ? displayData.categories.garden : []
    },
    latest: Array.isArray(displayData.latest) ? displayData.latest : []
  };

  // Ensure jobsData is always an array
  const safeJobsData = Array.isArray(jobsData) ? jobsData : [];

  // Loading component
  const LoadingCard = ({ type = 'card' }) => (
    <div className={`bg-gray-200 animate-pulse rounded-lg ${type === 'featured' ? 'h-64' : 'h-40'}`}></div>
  );

  // Error component
  const ErrorMessage = () => (
    <div className="error-message">
      <p>⚠️ {error}</p>
      <p>Đang hiển thị dữ liệu mẫu...</p>
    </div>
  );

  return (
    <main className="news-main">
      {/* API Status Indicator */}
      {apiStatus !== 'success' && (
        <div className={`api-status ${apiStatus}`}>
          {apiStatus === 'loading' && '🔄 Đang kết nối API...'}
          {apiStatus === 'error' && '❌ Lỗi kết nối API'}
        </div>
      )}

      {/* Hero Banner */}
      <div className="hero-banner">
        <img src="/images/contennt.png" alt="News Banner" className="hero-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>TIN TỨC & SỰ KIỆN</h1>
          <p>Cập nhật những tin tức mới nhất về công nghệ, kiến trúc và các dự án của PhucAn Solutions</p>
        </div>
      </div>

      <div className="news-container">
        {/* Error Message */}
        {error && <ErrorMessage />}
        
        <div className="news-grid">
          {/* Main Content */}
          <div className="main-content">
            
            {/* Tin Nổi Bật */}
            <section className="news-section">
              <h2 className="section-title">Tin Nổi Bật</h2>
              
              {loading ? (
                <LoadingCard type="featured" />
              ) : safeDisplayData.featured.length > 0 ? (
                <div className="featured-card">
                  <div className="featured-content">
                    <div className="featured-image">
                      <img src={getImageUrl(safeDisplayData.featured[0])} alt={safeDisplayData.featured[0].title} />
                    </div>
                    <div className="featured-text">
                      <div className="featured-meta">
                        <span className="category-badge">{safeDisplayData.featured[0].category_name}</span>
                        <span className="date">{formatDate(safeDisplayData.featured[0].date)}</span>
                      </div>
                      <h3>{safeDisplayData.featured[0].title}</h3>
                      <p>{safeDisplayData.featured[0].excerpt}</p>
                      <Link to={`/news/${safeDisplayData.featured[0].slug}`} className="read-more">
                        Đọc tiếp →
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-content">Chưa có tin nổi bật nào</div>
              )}
            </section>

            {/* Tuyển dụng */}
            <section className="news-section">
              <h2 className="section-title">
                💼 Tuyển dụng
                {jobsError && <span className="error-badge">⚠️ Lỗi API</span>}
              </h2>
              
              {jobsLoading ? (
                <div className="recruitment-grid">
                  {[...Array(2)].map((_, idx) => (
                    <LoadingCard key={idx} type="recruitment" />
                  ))}
                </div>
              ) : safeJobsData.length > 0 ? (
                <div className="recruitment-grid">
                  {safeJobsData.map(job => (
                    <div key={job.id} className="recruitment-card">
                      <div className="recruitment-image">
                        <img 
                          src={getJobImageUrl(job)} 
                          alt={`Tuyển dụng ${job.title}`}
                          onError={(e) => {
                            e.target.src = '/images';
                          }}
                        />
                        <div className="department-badge">
                          {getDepartmentName(job.department)}
                        </div>
                      </div>
                      <div className="recruitment-content">
                        <h3>{job.title}</h3>
                        <div className="recruitment-details">
                          <div className="detail-row">
                            <span>🏢 Công ty:</span>
                            <span>PhucAn Solutions</span>
                          </div>
                          <div className="detail-row">
                            <span>📍 Địa điểm:</span>
                            <span>{job.location}</span>
                          </div>
                          <div className="detail-row">
                            <span>💰 Mức lương:</span>
                            <span className="salary">{formatSalary(job.min_salary, job.max_salary)}</span>
                          </div>
                          <div className="detail-row">
                            <span>📋 Loại:</span>
                            <span>{getJobTypeName(job.job_type)}</span>
                          </div>
                          <div className="detail-row">
                            <span>⏰ Hạn nộp:</span>
                            <span>{formatDeadline(job.application_deadline)}</span>
                          </div>
                          {job.remote_allowed && (
                            <div className="detail-row">
                              <span>🌐 Remote:</span>
                              <span className="remote-badge">Có hỗ trợ</span>
                            </div>
                          )}
                        </div>
                        <div className="recruitment-actions">
                          <Link to={`/jobs/${job.slug}`} className="apply-btn">
                            Ứng tuyển ngay
                          </Link>
                          <Link to={`/jobs/${job.slug}`} className="view-details">
                            Xem chi tiết
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-content">
                  <p>🔍 Hiện tại chưa có vị trí tuyển dụng nào</p>
                  <Link to="/jobs" className="view-all-jobs">
                    Xem tất cả việc làm →
                  </Link>
                </div>
              )}
              
              {safeJobsData.length > 0 && (
                <div className="recruitment-footer">
                  <Link to="/jobs" className="view-all-jobs">
                    Xem tất cả việc làm →
                  </Link>
                </div>
              )}
            </section>

            {/* News Categories */}
            {[
              { key: 'iot', title: 'Tin IoT - Công nghệ' },
              { key: 'architecture', title: 'Tin Kiến trúc - Xây dựng' },
              { key: 'solar', title: 'Tin Năng lượng mặt trời', fallbackKey: 'energy' },
              { key: 'landscape', title: 'Tin Sân vườn - Cảnh quan', fallbackKey: 'garden' }
            ].map(({ key, title, fallbackKey }) => {
              // Use fallback key if main key is empty
              const categoryData = safeDisplayData.categories[key].length > 0 
                ? safeDisplayData.categories[key] 
                : (fallbackKey && safeDisplayData.categories[fallbackKey]) || [];

              return (
                <section key={key} className="news-section">
                  <h2 className="section-title">{title}</h2>
                  
                  {loading ? (
                    <div className="news-grid-3">
                      {[...Array(3)].map((_, idx) => <LoadingCard key={idx} />)}
                    </div>
                  ) : categoryData.length > 0 ? (
                    <div className="news-grid-3">
                      {categoryData.map(news => (
                        <Link key={news.id} to={`/news/${news.slug}`} className="news-card">
                          <div className="news-image">
                            <img src={getImageUrl(news)} alt={news.title} />
                          </div>
                          <div className="news-content">
                            <span className="news-date">{formatDate(news.date)}</span>
                            <h3>{news.title}</h3>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="no-content">Chưa có tin tức nào trong danh mục này</div>
                  )}
                </section>
              );
            })}

            {/* Video Section */}
            {/* <section className="news-section">
              <h2 className="section-title">Video</h2>
              <div className="video-card">
                <div className="video-thumbnail">
                  <img src="/images/news-video-thumbnail.jpg" alt="Video thumbnail" />
                  <div className="play-button">
                    <div className="play-icon"></div>
                  </div>
                </div>
                <div className="video-content">
                  <h3>Giới thiệu dự án Smart Home tại Phú Mỹ Hưng</h3>
                  <p>Khám phá hệ thống nhà thông minh hiện đại nhất Việt Nam với đầy đủ tính năng IoT, năng lượng mặt trời và sân vườn thông minh.</p>
                </div>
              </div>
            </section> */}
          </div>

          {/* Sidebar */}
          <div className="sidebar">
            
            {/* Tin mới nhất */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">Tin mới nhất</h3>
              
              {loading ? (
                <div className="latest-loading">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="latest-item-loading"></div>
                  ))}
                </div>
              ) : safeDisplayData.latest.length > 0 ? (
                <div className="latest-news">
                  {safeDisplayData.latest.map(news => (
                    <div key={news.id} className="latest-item">
                      <Link to={`/news/${news.slug}`} className="latest-title">
                        {news.title}
                      </Link>
                      <div className="latest-meta">
                        <span>{formatDate(news.date)}</span>
                        <span>{getViewsCount(news)?.toLocaleString()} lượt xem</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-content">Chưa có tin tức mới</div>
              )}
            </div>

            {/* AI Page */}
            {/* <div className="sidebar-card ai-card">
              <h3 className="sidebar-title">AI Page</h3>
              <div className="ai-content">
                <div className="ai-icon">
                  <span>AI</span>
                </div>
                <h4>Trợ lý AI thông minh</h4>
                <p>Đặt câu hỏi về các dự án, dịch vụ và giải pháp của chúng tôi</p>
                <button className="ai-btn">Bắt đầu chat</button>
              </div>
            </div> */}

            {/* Banner quảng cáo */}
            <div className="sidebar-card">

              <div className="banner-content">
                <h4>Tư vấn miễn phí</h4>
                <p>Liên hệ ngay để được tư vấn giải pháp phù hợp</p>
                <Link to="/contact" className="contact-btn">
                  Liên hệ ngay
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default News; 